import React, { useMemo } from 'react'
import { Typography } from '@mui/material'
import { Column } from '@material-table/core'
import {
  S3CheckinEntry,
  CheckinEntry,
  CheckinProduct,
  S3CheckinProduct,
} from '../../../app/types'
import {
  Address,
  createAddress
} from '../../../components/common/Address'
import ColumnCheckinButton from '../components/table/ColumnCheckinButton'
import { useResponsiveInfo } from '../../../hooks/useResponsiveInfo'
import { makeStyles } from 'tss-react/mui'
import { createLogger } from '../../../app/logger'
import Bib from '../../../components/common/Bib'

// eslint-disable-next-line no-unused-vars
const logger = createLogger({
  prefix: 'CheckinDataColumns'
})

export const convertToCheckinProduct = (p?: S3CheckinProduct): CheckinProduct => ({
  size: p?.size ?? '',
  quantity: +(p?.quantity ?? ''),
  name: p?.name ?? '',
  deliveryType: p?.deliveryType === 'EVENT_PICKUP'
    ? 'Pick Up'
    : p?.deliveryType === 'SHIP_ITEM'
      ? 'Shipped'
      : '',
})

export const convertToCheckinEntry = (e: S3CheckinEntry): CheckinEntry => ({
  entryId: e.entry_id,
  bib: e.entry_bib,
  tag: e.entry_tag,
  lastModifiedTime: e.entry_mtime,
  checkInTime: e.entry_check_in_time,
  checkedIn: !!e.entry_check_in,
  firstName: e.athlete_first_name,
  lastName: e.athlete_last_name,
  fullName: `${e.athlete_first_name} ${e.athlete_last_name}`,
  gender: e.athlete_sex,
  dob: e.athlete_dob,
  entryRaceAge: e.entry_race_age,
  entryType: e.entry_type,
  transId: e.entry_trans_id,
  eventId: e.event_id,
  genderBracketName: e.gender_bracket_name,
  standardProduct: convertToCheckinProduct(e.standard_product),
  itemsPurchased: e.items_purchased?.map(convertToCheckinProduct) ?? [],
  customQuestions: e.custom_questions ?? [],
  thirdPartyQuestions: e.third_party_questions ?? [],
  locationCity: e.location_city,
  locationCountry: e.location_country,
  locationRegion: e.location_region,
  raceId: e.race_id,
  raceName: e.race_name,
  regChoiceName: e.reg_choice_name,
  regOptionId: e.reg_option_id,
  regTransactionRegTime: e.reg_transaction_reg_time,
  waveName: e.wave_name,
  teamId: e.team_id,
  teamName: e.team_name,
  teamType: e.team_type,
  teamShareBib: e.team_share_bib,
  promoCode: e.promo_code,
  notes: e.entry_notes,
  primaryBracketName: e.primary_bracket_name,

  // PII - not storing/rendering
  athleteEmail: '',
  athleteHomePhone: '',
  athleteMobilePhone: null,
  genderBracketId: -1,
  locationPostalCode: '',
  locationStreet: '',
  locationStreet2: null,
  primaryBracketId: -1,
  entryExternalId: e.entry_external_id
})

const useStyles = makeStyles()(() => ({
  checkinColumnHeader: {}
}))

const CheckinDataColumns = (): Column<CheckinEntry>[] => {
  const { theme } = useStyles()
  const { isMobile } = useResponsiveInfo()

  const renderTeamData = (data: CheckinEntry) => {
    return (
      data.teamName
        ? <Typography style={{ color: '#00B0FF', fontSize: '0.8rem', fontStyle: 'italic' }} >{data.teamName}</Typography>
        : <></>
    )
  }

  return useMemo(() => {
    const cols: Column<CheckinEntry>[] = [
      {
        field: 'checkedIn',
        title: 'Checked-In',
        minWidth: 100,
        width: isMobile ? 150 : 100,
        render: (data: CheckinEntry) => {
          return (
            <ColumnCheckinButton
              entry={data} />
          )
        }
      },
      {
        field: 'fullName',
        title: 'Name',
        hidden: isMobile,
        render: (data: CheckinEntry) => {
          return (
            <>
            <Typography
              title={`Entry Id ${data.entryId}`}
              style={{
                color: '#00B0FF'
              }}
              className={'entry-row-first-name'}
            >
              {data.fullName}
            </Typography>
            { renderTeamData(data) }
            </>
          )
        }
      },
      {
        field: 'athleteInfo',
        title: 'Athlete Info',
        hidden: !isMobile,
        render: (data: CheckinEntry) => {
          return (
            <>
              <Typography
                title={`Entry Id ${data.entryId}`}
                style={{
                  color: '#00B0FF'
                }}
                className={'entry-row-last-name'}
              >
                {data.firstName} {data.lastName}, {data.gender} ({data.entryRaceAge})
              </Typography>
              { renderTeamData(data) }
              <Address lite={true} address={createAddress(data)} />
              <Typography
                title={`Entry Id ${data.entryId}`}
                color={'inherit'}
                className={'entry-row-race-name'}
              >
                {data.raceName}
              </Typography>
            </>
          )
        }
      },
      {
        field: 'bib',
        title: 'BIB #',
        width: /* isMobile ? '20%' : */ undefined,
        hidden: isMobile,
        render: (data: CheckinEntry) => {
          return (
            <Bib className={'entry-row-bib'} data={data} />
          )
        }
      },
      {
        field: 'raceName',
        title: 'Race Name',
        minWidth: 200,
        hidden: isMobile
      },
      {
        field: 'gender',
        title: 'Gender',
        hidden: isMobile
      },
      {
        field: 'address',
        title: 'Address',
        hidden: isMobile,
        minWidth: 300,
        render: (data: CheckinEntry) => {
          return (
            <Address lite={true} address={createAddress(data)} />
          )
        }
      },
      {
        field: 'entryRaceAge',
        title: 'Age',
        minWidth: 100,
        hidden: isMobile
      },
    ]
    return cols.map<Column<CheckinEntry>>(col => ({
      ...col,
      editable: 'never',
      headerStyle: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        fontWeight: 'bold',
        fontSize: 12
      }
    }))
  }, [
    isMobile,
    theme.palette.primary.contrastText,
    theme.palette.primary.main,
  ])
}

export default CheckinDataColumns
